import {
    Loader2,
    Clock,
    AlertCircle,
    ArrowDownFromLine,
    RefreshCw,
    Receipt,
    ListFilter,
    X,
    Calendar,
} from "lucide-react";

import { useUsers } from "@/hooks/useUsers";
import { useDocuments } from "@/features/documents";
import { useDocumentFilters } from "../hooks/useDocumentFilters";
import { useAtomValue } from "jotai";
import { useState, useMemo } from "react";
import { tagsAtom } from "@/features/tags/state/tagAtoms";
import { TagsHoverCard } from "@/features/tags/components/TagsHoverCard";
import { Input } from "@/components/ui/input";

import {
    CustomTagFilter,
    CustomTagFilterInput,
    CustomTagFilterOutput,
} from "./CustomTagFilter";
import {
    CustomUserFilter,
    CustomUserFilterInput,
    CustomUserFilterOutput,
} from "./CustomUserFilter";

import { DocumentModalBrowser } from "./DocumentModalBrowser";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button-new";
import IconButton from "@/components/ui/icon-button";
import { StatusBadge, type DocumentStatus } from "@/components/ui/status-badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

import type { Document } from "document-api-client";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { MonthSelector } from "@/components/MonthSelector";
import { cn } from "@/lib/utils";

import { format as dateFnsFormat } from "date-fns";
// Safe date formatting function
const formatDate = (
    dateValue: string | undefined | null,
    formatString: string,
    fallback: string = "Invalid date",
): string => {
    if (!dateValue) return fallback;
    const parsedDate = new Date(dateValue);
    if (isNaN(parsedDate.getTime())) {
        return fallback;
    }
    try {
        const formatedDate = dateFnsFormat(parsedDate, formatString);
        return formatedDate;
    } catch (e) {
        console.error(`Invalid date format: ${dateValue}`, e);
        return fallback;
    }
};

// Status-dependent content component
interface StatusContentProps {
    status: string;
    value: React.ReactNode;
}

const StatusContent = ({ status, value }: StatusContentProps) => {
    if (status === "COMPLETED") {
        return <>{value}</>;
    } else if (status === "FAILED") {
        return <AlertCircle className="inline-block h-4 w-4 text-red-300" />;
    } else {
        // QUEUED, PROCESSING, SUBMITTED
        return <Clock className="inline-block h-4 w-4 text-gray-300" />;
    }
};

export function DocumentTable() {
    const {
        documents,
        loading,
        error,
        pagination,
        loadNextPage,
        selectedMonth,
        changeMonth,
        goToPreviousMonth,
        goToNextMonth,
        refreshDocuments,
    } = useDocuments();

    const { users, getUserDisplayName } = useUsers();
    const tags = useAtomValue(tagsAtom);

    // Filter states with localStorage persistence
    const {
        selectedUserIds,
        setSelectedUserIds,
        selectedTagIds,
        setSelectedTagIds,
        approvalFilter,
        setApprovalFilter,
        searchQuery,
        setSearchQuery,
        clearFilters: clearAllFilters,
    } = useDocumentFilters();

    // Browser states
    const [viewDocumentMode, setViewDocumentMode] = useState(false);
    const [selectedDocumentIndex, setSelectedDocumentIndex] =
        useState<number>(0);

    // Handle document selection
    const handleViewDetails = (document: Document) => {
        const index = filteredDocuments.findIndex(
            (doc) => doc.documentId === document.documentId,
        );
        setSelectedDocumentIndex(index);
        setViewDocumentMode(true);
    };

    // Filter documents based on selected filters
    const filteredDocuments = useMemo(() => {
        return documents.filter((document) => {
            // Search filter
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const searchableText = [
                    document.vendorName,
                    document.invoiceReceiptId,
                    document.total?.toString(),
                    getUserDisplayName(document.createdBy || ""),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();

                if (!searchableText.includes(query)) {
                    return false;
                }
            }

            // User filter (OR logic)
            if (selectedUserIds.length > 0) {
                if (!selectedUserIds.includes(document.createdBy || "")) {
                    return false;
                }
            }

            // Tag filter (AND logic) - fileTags format: "tagId1#tagId2#tagId3"
            if (selectedTagIds.length > 0) {
                const docTagIds = document.fileTags?.split("#") || [];
                // Document must have ALL selected tags
                if (
                    !selectedTagIds.every((tagId) => docTagIds.includes(tagId))
                ) {
                    return false;
                }
            }

            // Approval filter
            if (approvalFilter === "pending") {
                // Show unapproved and completed documents
                if (document.isApproved || document.status !== "COMPLETED") {
                    return false;
                }
            } else if (approvalFilter === "approved") {
                // Show only approved documents
                if (!document.isApproved) {
                    return false;
                }
            }
            // "all" - no filtering by approval status

            return true;
        });
    }, [
        documents,
        searchQuery,
        selectedUserIds,
        selectedTagIds,
        approvalFilter,
        getUserDisplayName,
    ]);

    const hasActiveFilters =
        searchQuery.trim() !== "" ||
        selectedUserIds.length > 0 ||
        selectedTagIds.length > 0 ||
        approvalFilter !== "pending";

    const clearFilters = () => {
        clearAllFilters();
    };

    if (error) {
        return <div>Error: {error}</div>;
    }
    return (
        <div className="transition-all duration-200">
            {/* <div className="max-w-xl mb-1">
                <h1 className="text-5xl  sm:text-6xl font-medium text-md-on-surface leading-tight tracking-tight">
                    Receipts
                </h1>
                <p className="text-sm text-md-on-surface-variant leading-relaxed tracking-wide mt-1">
                    Review and approve receipts for expense reporting
                </p>
            </div> */}
            {/* Filter card */}
            <div className="flex gap-2 flex-col mb-4">
                <div className="w-full flex flex-row flex-nowrap items-center justify-between gap-1">
                    <div className="flex flex-row flex-nowrap items-center gap-4">
                        <Calendar className="inline-block size-4" />
                        <MonthSelector
                            selectedMonth={selectedMonth}
                            onMonthChange={changeMonth}
                            onPreviousMonth={goToPreviousMonth}
                            onNextMonth={goToNextMonth}
                        />
                    </div>
                </div>

                <div className="w-full flex flex-col gap-2 transition-all duration-200">
                    <div className="flex flex-row items-center gap-4">
                        <div>
                            <ListFilter className="inline-block size-4" />
                        </div>

                        <div className="flex flex-row flex-wrap items-center gap-1">
                            <Input
                                type="text"
                                placeholder="Search receipts..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-43.5"
                            />

                            <div className="inline-block">
                                <Select
                                    value={approvalFilter}
                                    onValueChange={(value) =>
                                        setApprovalFilter(
                                            value as
                                                | "all"
                                                | "pending"
                                                | "approved",
                                        )
                                    }
                                >
                                    <SelectTrigger className="w-33.5 h-10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        <SelectItem value="pending">
                                            Pending
                                        </SelectItem>
                                        <SelectItem value="approved">
                                            Approved
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <CustomUserFilter
                                users={users || []}
                                selectedUserIds={selectedUserIds}
                                onSelectedUsersChange={setSelectedUserIds}
                                getUserDisplayName={getUserDisplayName}
                            >
                                <CustomUserFilterInput className="w-43.5" />
                            </CustomUserFilter>
                            <CustomTagFilter
                                tags={tags}
                                selectedTagIds={selectedTagIds}
                                onSelectedTagsChange={setSelectedTagIds}
                            >
                                <CustomTagFilterInput className="w-43.5" />
                            </CustomTagFilter>

                            {hasActiveFilters && (
                                <Button
                                    variant="tertiary"
                                    size="sm"
                                    onClick={clearFilters}
                                    className="h-10 px-3"
                                >
                                    <X className="h-4 w-4 mr-0" />
                                    Clear
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-1 pl-8 min-h-[32px]">
                        <CustomUserFilter
                            users={users || []}
                            selectedUserIds={selectedUserIds}
                            onSelectedUsersChange={setSelectedUserIds}
                            getUserDisplayName={getUserDisplayName}
                        >
                            <CustomUserFilterOutput />
                        </CustomUserFilter>
                        <CustomTagFilter
                            tags={tags}
                            selectedTagIds={selectedTagIds}
                            onSelectedTagsChange={setSelectedTagIds}
                        >
                            <CustomTagFilterOutput />
                        </CustomTagFilter>
                    </div>
                </div>
            </div>

            <div className="flex flew-row justify-between items-end gap-8 mb-4">
                <div className="max-w-xl">
                    {/* <h1 className="text-2xl font-semibold text-md-on-surface leading-tight tracking-tight">
                        Receipts
                    </h1>
                    <p className="text-sm text-md-on-surface-variant leading-relaxed tracking-wide mt-1">
                        Review and approve receipts for expense reporting.
                    </p> */}
                </div>

                <IconButton
                    onClick={refreshDocuments}
                    disabled={loading}
                    size="sm"
                    color="secondary"
                    variant="outlined"
                >
                    <RefreshCw
                        className={cn("h-4 w-4", loading && "animate-spin")}
                    />
                </IconButton>
            </div>

            <Card className="p-0 animate-in fade-in-0 duration-200 transition-all border border-md-outline-variant">
                {documents.length === 0 && loading ? (
                    // <div className="text-center py-4">Loading documents...</div>
                    <div className="flex items-center justify-center py-12  animate-in fade-in-0">
                        <div className="flex items-center gap-2 text-md-on-surface-variant">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">
                                Loading Documents...
                            </span>
                        </div>
                    </div>
                ) : filteredDocuments.length === 0 ? (
                    <div className="text-center py-12 animate-in fade-in-0">
                        <div className="p-3 bg-md-surface-variant rounded-full w-fit mx-auto mb-3">
                            <Receipt className="h-6 w-6 text-md-on-surface-variant" />
                        </div>
                        <p className="text-md-on-surface-variant text-sm">
                            No documents found for{" "}
                            {selectedMonth
                                ? formatDate(
                                      `${selectedMonth}-01T00:00:00`,
                                      "MMMM yyyy",
                                      "the selected month",
                                  )
                                : "the selected month"}
                        </p>
                        {/* <p className="text-md-on-surface-variant text-xs mt-1">
                        Scan or upload new expense documents
                    </p> */}
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto max-w-full">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[140px] pl-6 py-3">
                                            Status
                                        </TableHead>
                                        <TableHead className="w-[140px] py-3">
                                            Created At
                                        </TableHead>
                                        <TableHead className="text-left py-3">
                                            Created By
                                        </TableHead>
                                        <TableHead className="w-[60px] text-center py-3">
                                            Tags
                                        </TableHead>
                                        <TableHead className="w-[100px] py-3">
                                            Receipt Date
                                        </TableHead>
                                        <TableHead className="w-[120px] py-3">
                                            Receipt ID
                                        </TableHead>
                                        <TableHead className="text-left py-3">
                                            Vendor
                                        </TableHead>
                                        <TableHead className="w-[100px] text-right pr-6 py-3">
                                            Total
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredDocuments.map((document) => (
                                        <TableRow
                                            key={document.documentId}
                                            className="cursor-pointer hover:bg-md-primary-container -odd:bg-white/50"
                                            onClick={() =>
                                                handleViewDetails(document)
                                            }
                                        >
                                            <TableCell className="w-[140px] pl-6">
                                                {document.isApproved ? (
                                                    <StatusBadge status="APPROVED" />
                                                ) : (
                                                    <StatusBadge
                                                        status={
                                                            document.status as DocumentStatus
                                                        }
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell className="w-[140px]">
                                                {formatDate(
                                                    document.createdAt,
                                                    `yyyy-MM-dd ${new Intl.DateTimeFormat().resolvedOptions().hour12 ? "hh:mm a" : "HH:mm"}`,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {document.createdBy
                                                    ? getUserDisplayName(
                                                          document.createdBy,
                                                      )
                                                    : "Unknown"}
                                            </TableCell>
                                            <TableCell className="w-[60px]">
                                                <StatusContent
                                                    status={document.status}
                                                    value={
                                                        document.fileTags ? (
                                                            <TagsHoverCard
                                                                fileTags={
                                                                    document.fileTags
                                                                }
                                                            />
                                                        ) : null
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell className="w-[100px]">
                                                <StatusContent
                                                    status={document.status}
                                                    value={formatDate(
                                                        document.invoiceReceiptDate,
                                                        "yyyy-MM-dd",
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell className="w-[120px]">
                                                <StatusContent
                                                    status={document.status}
                                                    value={
                                                        document.invoiceReceiptId ||
                                                        "-"
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell className="font-medium max-w-[200px] truncate">
                                                <StatusContent
                                                    status={document.status}
                                                    value={
                                                        document.vendorName ||
                                                        "-"
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell className="w-[100px] text-right pr-6">
                                                <StatusContent
                                                    status={document.status}
                                                    value={
                                                        <span className="font-semibold">
                                                            {document.total !== undefined
                                                                ? document.total.toFixed(2)
                                                                : "-"}
                                                        </span>
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        <div className="flex flex-col items-center gap-2 py-4">
                            <p className="text-sm text-md-on-surface-variant">
                                Showing {filteredDocuments.length} of{" "}
                                {documents.length} loaded documents
                                {pagination?.hasMore && " • More available"}
                            </p>
                            {pagination?.hasMore && (
                                <Button
                                    onClick={loadNextPage}
                                    disabled={loading}
                                    variant="secondary"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <ArrowDownFromLine className="mr-1 h-4 w-4 " />
                                            Load more
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </Card>
            {/* Document Browser Overlay */}
            <DocumentModalBrowser
                documents={filteredDocuments}
                open={viewDocumentMode}
                onClose={() => {
                    setViewDocumentMode(false);
                    // Refresh documents only when the overlay closes
                    refreshDocuments();
                }}
                onApproved={() => {
                    // Don't refresh here - will refresh on close
                }}
                onSaved={() => {
                    // Don't refresh here - will refresh on close
                }}
                title="Document Browser"
                initialIndex={selectedDocumentIndex}
            />
        </div>
    );
}
